import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import * as api from '@/features/backlog/api';
import * as searchApi from '@/features/search/api';
import { buildSearchRequest, isEmptyQuery, parseQuery, stringifyQuery } from '@/features/search/query';
import { useSavedSearches } from '@/features/search/saved';
import {
  SearchColKey,
  SearchColumnState,
  DEFAULT_SEARCH_COLUMNS,
  decodeCols,
  encodeVisibleCols,
  resolveSearchColumns,
  sanitizeSearchColumns,
  searchGridTemplate,
  searchTableMinWidth,
  visibleSearchKeys,
} from '@/features/search/columns';
import SearchColumnsMenu from '@/components/search-columns-menu';
import AncestorFlyout from './ancestor-flyout';
import { Board, Item, Status } from '@/features/backlog/types';
import { priorityMeta, taskTypeMeta } from '@/features/backlog/constants';
import { timeAgo } from '@/features/backlog/format';
import { searchPath, taskPath } from '@/constants/routes';
import { Chevron, PriorityChevron, Search, Tag, TaskTypeGlyph, X } from '@/components/ui/icons';
import MemberAvatar from '@/components/member-avatar';
import { cn } from '@/components/ui/cn';
import { useDocumentTitle } from '@/utils/use-document-title';

const humanize = (v: string) => v.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
/** Rows fetched per page; pagination (server OFFSET) walks the full result set past this. */
const PAGE_SIZE = 50;

type Sort = { key: string | null; dir: 'asc' | 'desc' };
const encodeSort = (s: Sort): string => (s.key ? `${s.key}:${s.dir}` : '');
const decodeSort = (raw: string | null): Sort => {
  if (!raw) return { key: null, dir: 'asc' };
  const [key, dir] = raw.split(':');
  return { key: key || null, dir: dir === 'desc' ? 'desc' : 'asc' };
};

/** Cross-board global search results (?query=…) as a sortable, column-customizable table. */
export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('query') ?? '';
  const navigate = useNavigate();
  const { boards, members } = useAppData();
  const { me } = useIdentity();
  const { save } = useSavedSearches();

  const [input, setInput] = useState(q);
  const [results, setResults] = useState<Item[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [labels, setLabels] = useState<string[]>([]);
  const [statusMap, setStatusMap] = useState<Map<string, Map<string, Status>>>(new Map());
  const [naming, setNaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [prefsCols, setPrefsCols] = useState<SearchColumnState | null>(null);
  // Resolved epic / parent items (by id) for the Epic / Parent columns + ancestor flyout, and the item
  // whose ancestor tree is open.
  const [refMap, setRefMap] = useState<Map<string, Item>>(new Map());
  const [treeFor, setTreeFor] = useState<{ item: Item; rect: DOMRect } | null>(null);
  const inFlight = useRef(0);

  // The table VIEW (columns + sort) lives in the URL so a shared link reproduces it exactly. Columns
  // fall back to the member's personal default, then the built-in default, until the URL carries them.
  const colsStr = params.get('cols');
  const sortStr = params.get('sort');
  const cols = useMemo<SearchColumnState>(() => (colsStr ? decodeCols(colsStr) : prefsCols ?? DEFAULT_SEARCH_COLUMNS), [colsStr, prefsCols]);
  const sort = useMemo<Sort>(() => decodeSort(sortStr), [sortStr]);
  const pageParam = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useDocumentTitle(q ? `Search · ${q}` : 'Search');

  const boardById = useMemo(() => new Map(boards.map((b) => [b.id, b])), [boards]);
  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const ctx = useMemo(() => ({ boards, members, meId: me?.id, labels }), [boards, members, me, labels]);

  useEffect(() => { api.listLabels().then(setLabels).catch(() => undefined); }, []);
  useEffect(() => setInput(q), [q]);
  // Load the member's personal default column layout once.
  useEffect(() => { api.getMyPreferences().then((p) => setPrefsCols(sanitizeSearchColumns(p.search_columns))).catch(() => undefined); }, []);

  // Seed the URL with the effective columns once (when a query is present but the link doesn't carry
  // cols yet) so copying the URL always shares the exact table.
  useEffect(() => {
    if (isEmptyQuery(q) || colsStr || !prefsCols) return;
    setParams((prev) => { const p = new URLSearchParams(prev); p.set('cols', encodeVisibleCols(prefsCols)); return p; }, { replace: true });
  }, [q, colsStr, prefsCols, setParams]);

  // Column / sort changes write straight to the URL (shareable); columns also update the personal default.
  const setColsUrl = (next: SearchColumnState) => {
    setParams((prev) => { const p = new URLSearchParams(prev); p.set('cols', encodeVisibleCols(next)); return p; }, { replace: true });
    api.saveMyPreferences({ search_columns: next }).catch(() => undefined);
  };
  // Sort changes reset to page 1 (the old page may not exist for the new ordering's… it does, but page
  // 1 is the natural landing when you re-sort).
  const setSort: React.Dispatch<React.SetStateAction<Sort>> = (updater) => {
    const next = typeof updater === 'function' ? (updater as (s: Sort) => Sort)(sort) : updater;
    setParams((prev) => { const p = new URLSearchParams(prev); const s = encodeSort(next); if (s) p.set('sort', s); else p.delete('sort'); p.delete('page'); return p; }, { replace: true });
  };
  const setPage = (n: number) => {
    setParams((prev) => { const p = new URLSearchParams(prev); p.set('page', String(Math.max(1, n))); return p; });
    window.scrollTo({ top: 0 }); // best-effort: jump to the top of the fresh page
  };

  // Run the search whenever the query, page, or sort changes. Sorting AND paging are both server-side
  // so a column sort orders the WHOLE result set and OFFSET walks it (not just the visible page).
  useEffect(() => {
    if (isEmptyQuery(q)) { setResults([]); setTotal(0); return; }
    const seq = ++inFlight.current;
    setLoading(true);
    const req = buildSearchRequest(parseQuery(q), ctx, PAGE_SIZE, (pageParam - 1) * PAGE_SIZE);
    const sortConds = sort.key ? [{ name: sort.key, dir: sort.dir }] : [];
    searchApi
      .searchItems({ ...req, sort: sortConds }) // no sort → backend relevance (text) / most-recent (clauses only)
      .then((r) => { if (seq === inFlight.current) { setResults(r.data); setTotal(r.total); } })
      .catch(() => { if (seq === inFlight.current) { setResults([]); setTotal(0); } })
      .finally(() => { if (seq === inFlight.current) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, ctx, pageParam, sortStr]);

  // If the active page fell past the end (e.g. the query narrowed the result set), snap to the last page.
  useEffect(() => {
    if (total > 0 && pageParam > pageCount) {
      setParams((prev) => { const p = new URLSearchParams(prev); p.set('page', String(pageCount)); return p; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, pageParam, pageCount]);

  // Lazily fetch per-board statuses for the boards present in the results (labels + colours).
  useEffect(() => {
    const need = new Set<string>();
    results.forEach((it) => { if (!statusMap.has(it.board_id)) need.add(it.board_id); });
    if (!need.size) return;
    need.forEach((bid) => {
      api.listStatuses(bid)
        .then((sts) => setStatusMap((cur) => new Map(cur).set(bid, new Map(sts.map((s) => [s.value, s])))))
        .catch(() => setStatusMap((cur) => new Map(cur).set(bid, new Map())));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  // Batch-resolve the epic / parent items referenced by this page (for the Epic / Parent columns). One
  // request per page; already-known ids are skipped so paging back and forth doesn't re-fetch.
  useEffect(() => {
    const want = new Set<string>();
    results.forEach((it) => { if (it.epic_id) want.add(it.epic_id); if (it.parent_id) want.add(it.parent_id); });
    const need = [...want].filter((id) => !refMap.has(id));
    if (need.length === 0) return;
    api.itemsByIds(need)
      .then((items) => setRefMap((cur) => { const m = new Map(cur); items.forEach((it) => m.set(it.id, it)); return m; }))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const visibleCols = useMemo(() => resolveSearchColumns(visibleSearchKeys(cols)), [cols]);
  const gridTemplate = useMemo(() => searchGridTemplate(visibleCols), [visibleCols]);
  const minWidth = useMemo(() => searchTableMinWidth(visibleCols), [visibleCols]);

  const statusOf = (it: Item) => statusMap.get(it.board_id)?.get(it.status);

  // Preserve the table view (cols/sort) when the mobile input changes the query; a cleared query
  // resets the whole view. Either way the page returns to 1 for the new query.
  const submit = (v: string) =>
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (v.trim()) p.set('query', v.trim());
      else { p.delete('query'); p.delete('cols'); p.delete('sort'); }
      p.delete('page');
      return p;
    });
  const doSave = () => {
    const name = nameDraft.trim() || q.trim();
    if (!name) return;
    save(name, q, { cols: encodeVisibleCols(cols), sort: encodeSort(sort) || undefined });
    setNaming(false);
    setNameDraft('');
  };
  const tokens = useMemo(() => parseQuery(q), [q]);
  // Removing a chip rebuilds the query from the remaining tokens/text (keeps the table view unless the
  // query becomes empty). submit() also snaps back to page 1.
  const removeToken = (i: number) => submit(stringifyQuery({ text: tokens.text, tokens: tokens.tokens.filter((_, idx) => idx !== i) }));
  const removeText = () => submit(stringifyQuery({ text: '', tokens: tokens.tokens }));

  const renderCell = (key: SearchColKey, it: Item) => {
    switch (key) {
      case 'board':
        return <span className="truncate rounded-full bg-layer-strong px-2 py-0.5 text-[11px] font-medium text-fg-faint">{boardById.get(it.board_id)?.name ?? '—'}</span>;
      case 'status': {
        const st = statusOf(it);
        return (
          <span className="inline-flex min-w-0 items-center gap-1.5 text-[12px] text-fg-soft">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: st?.color || '#71717a' }} />
            <span className="truncate">{st?.label ?? humanize(it.status)}</span>
          </span>
        );
      }
      case 'priority': {
        const pm = priorityMeta(it.priority);
        return <span className="flex h-4 w-4 items-center justify-center" title={pm.label}><PriorityChevron level={pm.level} color={pm.color} /></span>;
      }
      case 'assignee': {
        const m = it.assignee_id ? memberById.get(it.assignee_id) : undefined;
        return (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <MemberAvatar member={m} size={20} className="shrink-0" />
            <span className="truncate text-[12px] text-fg-soft">{m?.name ?? 'Unassigned'}</span>
          </span>
        );
      }
      case 'type': {
        const tm = taskTypeMeta(it.type);
        return <span className="inline-flex items-center gap-1.5 text-[12px] text-fg-soft"><TaskTypeGlyph type={it.type} color={tm.color} />{tm.label}</span>;
      }
      case 'epic':
        if (!it.epic_id) return <span className="text-[12px] text-fg-ghost">—</span>;
        return <RefCell ref_={refMap.get(it.epic_id)} boardById={boardById} />;
      case 'parent':
        if (!it.parent_id) return <span className="text-[12px] text-fg-ghost">—</span>;
        return <RefCell ref_={refMap.get(it.parent_id)} boardById={boardById} />;
      case 'labels':
        return (
          <span className="flex min-w-0 flex-wrap gap-1">
            {(it.labels ?? []).slice(0, 3).map((l) => (
              <span key={l} className="truncate rounded-full bg-accent/12 px-1.5 py-0.5 text-[10.5px] font-medium text-accent">{l}</span>
            ))}
            {(it.labels?.length ?? 0) > 3 ? <span className="text-[10.5px] text-fg-ghost">+{(it.labels!.length) - 3}</span> : null}
          </span>
        );
      case 'source':
        return <span className="truncate text-[12px] text-fg-faint">{(it.source ?? []).join(', ')}</span>;
      case 'created':
        return <span className="text-[11.5px] text-fg-ghost">{timeAgo(it.created_at)}</span>;
      case 'updated':
        return <span className="text-[11.5px] text-fg-ghost">{timeAgo(it.updated_at)}</span>;
      default:
        return null;
    }
  };

  return (
    // Full-width: the results table (and its column set) should use the whole content area on wide
    // monitors, not a 1200px column. The <main> wrapper already supplies page padding + sidebar/dock inset.
    <div className="w-full">
      <div className="mb-4">
        {/* Mobile only: header search collapses to an icon < md, so this input covers that width. */}
        <div className="flex items-center gap-2 md:hidden">
          <div className="flex h-11 flex-1 items-center gap-2 rounded-xl border border-line bg-surface-inset px-3 focus-within:border-accent/50">
            <Search className="h-4.5 w-4.5 shrink-0 text-fg-faint" />
            <input
              autoFocus
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(input); }}
              placeholder="Search all boards…"
              className="min-w-0 flex-1 bg-transparent text-[14px] text-fg placeholder:text-fg-ghost focus:outline-none"
            />
            <button type="button" onClick={() => submit(input)} className="shrink-0 rounded-lg bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-ink">Search</button>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-fg-faint md:mt-0">
          {!isEmptyQuery(q) ? <span className="font-medium text-fg-soft">{loading ? 'Searching…' : `${total} result${total === 1 ? '' : 's'}`}</span> : null}
          {tokens.tokens.map((t, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-layer py-0.5 pl-2 pr-1 font-mono text-[11px] text-fg-soft">
              {t.negate ? <span className="text-danger">−</span> : null}
              {t.field}:{t.values.join(',')}
              <button type="button" onClick={() => removeToken(i)} aria-label={`Remove ${t.field} filter`} title="Remove filter" className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-fg-ghost transition-colors hover:bg-layer-strong hover:text-fg">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {tokens.text ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-layer py-0.5 pl-2 pr-1 text-[11px] text-fg-soft">
              “{tokens.text}”
              <button type="button" onClick={removeText} aria-label="Remove text filter" title="Remove filter" className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-fg-ghost transition-colors hover:bg-layer-strong hover:text-fg">
                <X className="h-3 w-3" />
              </button>
            </span>
          ) : null}
          {!isEmptyQuery(q) ? (
            <span className="ml-auto flex items-center gap-2">
              <SearchColumnsMenu order={cols.order} hidden={cols.hidden} onReorder={(order) => setColsUrl({ ...cols, order })} onToggle={(k) => setColsUrl({ ...cols, hidden: cols.hidden.includes(k) ? cols.hidden.filter((x) => x !== k) : [...cols.hidden, k] })} />
              {naming ? (
                <span className="inline-flex items-center gap-1.5">
                  <input autoFocus value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doSave(); if (e.key === 'Escape') setNaming(false); }} placeholder="Name…" className="w-40 rounded-lg border border-line bg-surface-inset px-2 py-1 text-[12px] text-fg focus:border-accent/50 focus:outline-none" />
                  <button type="button" onClick={doSave} className="rounded-lg bg-accent px-2.5 py-1 text-[12px] font-semibold text-ink">Save</button>
                </span>
              ) : (
                <button type="button" onClick={() => { setNaming(true); setNameDraft(''); }} className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1 text-[12px] font-medium text-fg-soft transition-colors hover:bg-layer">
                  <Tag className="h-3.5 w-3.5" /> Save search
                </button>
              )}
            </span>
          ) : null}
        </div>
      </div>

      {isEmptyQuery(q) ? (
        <div className="rounded-2xl border border-dashed border-line px-6 py-16 text-center text-[13px] text-fg-faint">
          Search across every board from the bar above (<kbd className="rounded border border-line px-1 text-[11px]">⌘K</kbd>). Combine filters like{' '}
          <span className="font-mono text-fg-soft">assignee:me priority:high,blocker -status:Done</span>.
        </div>
      ) : results.length === 0 && !loading && pageParam <= pageCount ? (
        <div className="rounded-2xl border border-dashed border-line px-6 py-16 text-center text-[13px] text-fg-faint">No items match this search.</div>
      ) : (
        <div className="thin-scroll overflow-x-auto rounded-2xl border border-line">
          <div style={{ minWidth }}>
            {/* Header */}
            <div className="grid items-center border-b border-line bg-surface-inset px-2 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-fg-faint" style={{ gridTemplateColumns: gridTemplate }}>
              <div className="px-1"><SortHeader label="Item" k="title" sort={sort} setSort={setSort} /></div>
              {visibleCols.map((col) => (
                <div key={col.key} className={cn('px-1', col.align === 'center' && 'text-center')}>
                  {col.sortable ? <SortHeader label={col.label} k={col.key} sort={sort} setSort={setSort} /> : col.label}
                </div>
              ))}
            </div>
            {/* Rows (already ordered server-side to match the active sort + pagination) */}
            {results.map((it) => {
              const board = boardById.get(it.board_id);
              return (
                <a
                  key={it.id}
                  href={board ? taskPath(board.slug, it.key) : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="grid w-full items-center border-b border-line px-2 py-2 text-left transition-colors last:border-0 hover:bg-layer"
                  style={{ gridTemplateColumns: gridTemplate }}
                >
                  <div className="flex min-w-0 items-center gap-2 px-1">
                    {it.parent_id ? (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTreeFor({ item: it, rect: e.currentTarget.getBoundingClientRect() }); }}
                        title="Show ancestor tree"
                        className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-fg-ghost transition-colors hover:bg-layer-strong hover:text-accent"
                      >
                        <TreeIcon />
                      </button>
                    ) : (
                      <span className="h-4 w-4 shrink-0" />
                    )}
                    <TaskTypeGlyph type={it.type} color={taskTypeMeta(it.type).color} />
                    <span className="item-key shrink-0 rounded px-1.5 py-0.5 tnum text-[11px] font-semibold">{it.key}</span>
                    <span className="min-w-0 truncate text-[13.5px] text-fg">{it.title || 'Untitled'}</span>
                  </div>
                  {visibleCols.map((col) => (
                    <div key={col.key} className={cn('flex min-w-0 items-center px-1', col.align === 'center' && 'justify-center')}>
                      {renderCell(col.key, it)}
                    </div>
                  ))}
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* pageParam <= pageCount guards the one-frame inverted range when deep-linking an out-of-range
          ?page (the clamp effect snaps it back on the next frame). */}
      {!isEmptyQuery(q) && total > 0 && pageParam <= pageCount ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[12px] text-fg-faint">
          <span>
            {loading
              ? 'Searching…'
              : `Showing ${(pageParam - 1) * PAGE_SIZE + 1}–${Math.min(pageParam * PAGE_SIZE, total)} of ${total}`}
          </span>
          {pageCount > 1 ? (
            <span className="flex items-center gap-1">
              <button type="button" disabled={pageParam <= 1} onClick={() => setPage(pageParam - 1)} className="rounded-lg border border-line px-2.5 py-1 font-medium text-fg-soft transition-colors hover:bg-layer disabled:cursor-not-allowed disabled:opacity-40">Prev</button>
              <span className="px-1.5 tnum text-fg-soft">Page {pageParam} / {pageCount}</span>
              <button type="button" disabled={pageParam >= pageCount} onClick={() => setPage(pageParam + 1)} className="rounded-lg border border-line px-2.5 py-1 font-medium text-fg-soft transition-colors hover:bg-layer disabled:cursor-not-allowed disabled:opacity-40">Next</button>
            </span>
          ) : null}
        </div>
      ) : null}

      {!isEmptyQuery(q) ? (
        <div className="mt-4 text-center text-[11.5px] text-fg-ghost">
          <button type="button" onClick={() => navigate(searchPath(''))} className="hover:text-fg-soft">Clear</button>
        </div>
      ) : null}

      {treeFor ? <AncestorFlyout item={treeFor.item} rect={treeFor.rect} boardById={boardById} onClose={() => setTreeFor(null)} /> : null}
    </div>
  );
}

/** A small hierarchy glyph shown before items that have a parent — opens the ancestor tree. */
function TreeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="5" rx="1" /><rect x="14" y="9" width="7" height="5" rx="1" /><rect x="14" y="16" width="7" height="5" rx="1" />
      <path d="M6.5 8v6.5a2 2 0 0 0 2 2H14M14 11.5H10" />
    </svg>
  );
}

/** A compact epic / parent reference cell (glyph + key + title), click-through to that item (new tab).
 *  A span, not an <a>, so it can live inside the row's link without nesting anchors. */
function RefCell({ ref_, boardById }: { ref_?: Item; boardById: Map<string, Board> }) {
  if (!ref_) return <span className="text-[11px] text-fg-ghost">…</span>;
  const b = boardById.get(ref_.board_id);
  const open = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); if (b) window.open(taskPath(b.slug, ref_.key), '_blank', 'noopener,noreferrer'); };
  return (
    <span
      onClick={b ? open : undefined}
      title={ref_.title}
      className={cn('flex min-w-0 items-center gap-1.5 text-[12px] text-fg-soft', b && 'cursor-pointer hover:text-fg')}
    >
      <TaskTypeGlyph type={ref_.type} color={taskTypeMeta(ref_.type).color} />
      <span className="item-key shrink-0 rounded px-1 py-px tnum text-[10px] font-semibold">{ref_.key}</span>
      <span className="min-w-0 flex-1 truncate">{ref_.title || 'Untitled'}</span>
    </span>
  );
}

/** A clickable column header that toggles the table's client-side sort (mirrors the board Table). */
function SortHeader({ label, k, sort, setSort }: { label: string; k: string; sort: Sort; setSort: React.Dispatch<React.SetStateAction<Sort>> }) {
  const active = sort.key === k;
  return (
    <button
      type="button"
      onClick={() => setSort((s) => (s.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' }))}
      className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-fg-soft"
    >
      {label}
      <Chevron className={cn('h-3 w-3 rotate-90 transition-transform', active ? (sort.dir === 'asc' ? 'rotate-[270deg] text-accent' : 'rotate-90 text-accent') : 'opacity-30')} />
    </button>
  );
}
